import { Fragment, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Coins,
  ExternalLink,
  Eye,
  FolderOpen,
  Shield,
  Trash2,
  X,
} from 'lucide-react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import Seo from '../seo/Seo';
import { useAuth } from '../context/AuthContext';
import {
  deleteAdminUser,
  getAdminUserProjects,
  getAdminUsers,
  previewIdleZeroCreditCleanup,
  runIdleZeroCreditCleanup,
  updateAdminUserCredits,
} from '../lib/requestAdminApi';

const CLEANUP_CONFIRM_PHRASE = 'DELETE_IDLE_ZERO_CREDIT_PROJECTS';
const USER_TABLE_COL_COUNT = 9;

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatDateShort(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function truncateEmail(email, maxLen = 26) {
  if (!email) return '—';
  if (email.length <= maxLen) return email;
  return `${email.slice(0, maxLen - 1)}…`;
}

function truncateUid(uid) {
  if (!uid || uid.length <= 12) return uid || '—';
  return `${uid.slice(0, 8)}…${uid.slice(-4)}`;
}

/** @typedef {'email' | 'displayName' | 'creationTime' | 'lastSignInTime' | 'emailVerified' | 'credits' | 'projectCount'} AdminUserSortKey */

/**
 * @param {Record<string, unknown>} row
 * @param {AdminUserSortKey} key
 */
function sortValueForUser(row, key) {
  switch (key) {
    case 'email':
      return String(row.email || '').toLowerCase();
    case 'displayName':
      return String(row.displayName || '').toLowerCase();
    case 'creationTime':
      return Date.parse(String(row.creationTime || '')) || 0;
    case 'lastSignInTime':
      return Date.parse(String(row.lastSignInTime || '')) || 0;
    case 'emailVerified':
      return row.emailVerified ? 1 : 0;
    case 'credits':
      return Number(row.credits) || 0;
    case 'projectCount':
      return Number(row.projectCount) || 0;
    default:
      return 0;
  }
}

function SortableHeader({ label, sortKey, activeSortKey, sortDir, onSort, className = '' }) {
  const isActive = activeSortKey === sortKey;
  const Icon = !isActive ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  const ariaSort = isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <th aria-sort={ariaSort} className={className || undefined}>
      <button
        type="button"
        className={`admin-sort-header${isActive ? ' is-active' : ''}`}
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <Icon size={14} strokeWidth={2} aria-hidden />
      </button>
    </th>
  );
}

/**
 * @param {{
 *   loading: boolean,
 *   error: string,
 *   projects: Array<Record<string, unknown>>,
 *   onRetry: () => void,
 *   onViewImage: (url: string, label: string) => void,
 * }} props
 */
function AdminUserProjectsExpand({ loading, error, projects, onRetry, onViewImage }) {
  if (loading) {
    return (
      <p className="admin-dashboard-status admin-projects-expand-content" role="status">
        Loading projects…
      </p>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-error admin-projects-expand-content" role="alert">
        <p>{error}</p>
        <button type="button" className="btn btn-outline btn-sm" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <p className="admin-dashboard-status admin-projects-expand-content">No projects for this user.</p>
    );
  }

  return (
    <div className="admin-projects-expand-content">
      <table className="admin-users-table admin-projects-table">
        <thead>
          <tr>
            <th>Project ID</th>
            <th>Created</th>
            <th>Status</th>
            <th>Room / Style</th>
            <th>Original</th>
            <th>Generated</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={String(project.id)}>
              <td>
                <code className="admin-uid" title={String(project.id)}>
                  {truncateUid(String(project.id))}
                </code>
              </td>
              <td>{formatDate(project.createdAt)}</td>
              <td>{project.status || '—'}</td>
              <td>
                {[project.roomType, project.style].filter(Boolean).join(' / ') || '—'}
              </td>
              <td>
                {project.originalImageUrl ? (
                  <button
                    type="button"
                    className="admin-icon-btn admin-icon-btn--view"
                    onClick={() => onViewImage(String(project.originalImageUrl), 'Original image')}
                    aria-label="View original image"
                    title="View original image"
                  >
                    <Eye size={15} strokeWidth={2} aria-hidden />
                  </button>
                ) : (
                  '—'
                )}
              </td>
              <td>
                {project.generatedImageUrl ? (
                  <button
                    type="button"
                    className="admin-icon-btn admin-icon-btn--view"
                    onClick={() => onViewImage(String(project.generatedImageUrl), 'Generated image')}
                    aria-label="View generated image"
                    title="View generated image"
                  >
                    <Eye size={15} strokeWidth={2} aria-hidden />
                  </button>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [selectedUserUid, setSelectedUserUid] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [creditsModalUser, setCreditsModalUser] = useState(null);
  const [creditsInput, setCreditsInput] = useState('');
  const [creditsSaving, setCreditsSaving] = useState(false);
  const [creditsModalError, setCreditsModalError] = useState('');
  const [deleteModalUser, setDeleteModalUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteModalError, setDeleteModalError] = useState('');
  const [cleanupPreviewLoading, setCleanupPreviewLoading] = useState(false);
  const [cleanupPreviewError, setCleanupPreviewError] = useState('');
  const [cleanupRunModalOpen, setCleanupRunModalOpen] = useState(false);
  const [cleanupConfirmText, setCleanupConfirmText] = useState('');
  const [cleanupRunSaving, setCleanupRunSaving] = useState(false);
  const [cleanupRunError, setCleanupRunError] = useState('');
  const [lastCleanupResult, setLastCleanupResult] = useState(null);
  /** @type {[AdminUserSortKey, import('react').Dispatch<import('react').SetStateAction<AdminUserSortKey>>]} */
  const [sortKey, setSortKey] = useState('creationTime');
  const [sortDir, setSortDir] = useState('desc');
  const [imagePreview, setImagePreview] = useState(null);
  const creditsDialogTitleId = useId();
  const creditsDialogDescId = useId();
  const deleteDialogTitleId = useId();
  const deleteDialogDescId = useId();
  const cleanupRunDialogTitleId = useId();
  const cleanupRunDialogDescId = useId();

  const loadUsers = useCallback(async () => {
    if (!user) return;
    setUsersLoading(true);
    setUsersError('');
    const result = await getAdminUsers(user);
    if (result.ok) {
      setUsers(Array.isArray(result.data?.users) ? result.data.users : []);
    } else {
      setUsers([]);
      setUsersError(result.error);
    }
    setUsersLoading(false);
  }, [user]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const sortedUsers = useMemo(() => {
    const rows = [...users];
    rows.sort((a, b) => {
      const aVal = sortValueForUser(a, sortKey);
      const bVal = sortValueForUser(b, sortKey);
      let cmp = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal);
      } else {
        cmp = Number(aVal) - Number(bVal);
      }
      if (cmp === 0) {
        cmp = String(a.email || '').localeCompare(String(b.email || ''));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [users, sortKey, sortDir]);

  /** @param {AdminUserSortKey} key */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'creationTime' || key === 'lastSignInTime' ? 'desc' : 'asc');
  };

  const handleViewProjects = async (uid) => {
    if (!user) return;

    if (selectedUserUid === uid) {
      setSelectedUserUid(null);
      setProjects([]);
      setProjectsError('');
      return;
    }

    setSelectedUserUid(uid);
    setProjects([]);
    setProjectsLoading(true);
    setProjectsError('');
    const result = await getAdminUserProjects(user, uid);
    if (result.ok) {
      setProjects(Array.isArray(result.data?.projects) ? result.data.projects : []);
    } else {
      setProjectsError(result.error);
    }
    setProjectsLoading(false);
  };

  const openCreditsModal = (row) => {
    setCreditsModalUser(row);
    setCreditsInput(String(row.credits ?? 0));
    setCreditsModalError('');
  };

  const closeCreditsModal = () => {
    if (creditsSaving) return;
    setCreditsModalUser(null);
    setCreditsInput('');
    setCreditsModalError('');
  };

  const submitCredits = async () => {
    if (!user || !creditsModalUser) return;
    const parsed = Number(creditsInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setCreditsModalError('Enter a valid number greater than or equal to 0.');
      return;
    }
    setCreditsSaving(true);
    setCreditsModalError('');
    const result = await updateAdminUserCredits(user, creditsModalUser.uid, parsed);
    setCreditsSaving(false);
    if (result.ok) {
      const nextCredits = result.data?.credits ?? parsed;
      setUsers((prev) =>
        prev.map((row) =>
          row.uid === creditsModalUser.uid
            ? {
                ...row,
                credits: nextCredits,
                firestoreUserData: { ...row.firestoreUserData, credits: nextCredits },
              }
            : row
        )
      );
      setCreditsModalUser(null);
      setCreditsInput('');
      setCreditsModalError('');
    } else {
      setCreditsModalError(result.error);
    }
  };

  const openDeleteModal = (row) => {
    setDeleteModalUser(row);
    setDeleteConfirmText('');
    setDeleteModalError('');
  };

  const closeDeleteModal = () => {
    if (deleteSaving) return;
    setDeleteModalUser(null);
    setDeleteConfirmText('');
    setDeleteModalError('');
  };

  const submitDelete = async () => {
    if (!user || !deleteModalUser) return;
    if (deleteConfirmText.trim() !== 'DELETE') {
      setDeleteModalError('Type DELETE to confirm.');
      return;
    }
    setDeleteSaving(true);
    setDeleteModalError('');
    const result = await deleteAdminUser(user, deleteModalUser.uid);
    setDeleteSaving(false);
    if (result.ok) {
      setUsers((prev) => prev.filter((row) => row.uid !== deleteModalUser.uid));
      if (selectedUserUid === deleteModalUser.uid) {
        setSelectedUserUid(null);
        setProjects([]);
      }
      setDeleteModalUser(null);
      setDeleteConfirmText('');
      setDeleteModalError('');
    } else {
      setDeleteModalError(result.error);
    }
  };

  const handleCleanupPreview = async () => {
    if (!user) return;
    setCleanupPreviewLoading(true);
    setCleanupPreviewError('');
    const result = await previewIdleZeroCreditCleanup(user);
    setCleanupPreviewLoading(false);
    if (result.ok) {
      setLastCleanupResult(result.data);
    } else {
      setCleanupPreviewError(result.error);
    }
  };

  const openCleanupRunModal = () => {
    setCleanupRunModalOpen(true);
    setCleanupConfirmText('');
    setCleanupRunError('');
  };

  const closeCleanupRunModal = () => {
    if (cleanupRunSaving) return;
    setCleanupRunModalOpen(false);
    setCleanupConfirmText('');
    setCleanupRunError('');
  };

  const submitCleanupRun = async () => {
    if (!user) return;
    if (cleanupConfirmText.trim() !== CLEANUP_CONFIRM_PHRASE) {
      setCleanupRunError(`Type ${CLEANUP_CONFIRM_PHRASE} to confirm.`);
      return;
    }
    setCleanupRunSaving(true);
    setCleanupRunError('');
    const result = await runIdleZeroCreditCleanup(user);
    setCleanupRunSaving(false);
    if (result.ok) {
      setLastCleanupResult(result.data);
      setCleanupRunModalOpen(false);
      setCleanupConfirmText('');
      setCleanupRunError('');
      loadUsers();
    } else {
      setCleanupRunError(result.error);
    }
  };

  return (
    <div className="page landing-bg">
      <Seo
        title="Admin | MyRoomsAI"
        description="MyRoomsAI admin tools and user management."
        canonicalPath="/admin"
        noindex
      />
      <Navbar />
      <main className="container admin-page">
        <div className="admin-page-header">
          <Shield size={28} strokeWidth={2} aria-hidden />
          <div>
            <h1>Admin</h1>
            <p className="admin-page-subtitle">Signed in as {user?.email}</p>
          </div>
        </div>

        <section className="admin-page-section admin-dashboard-section">
          <div className="admin-dashboard-section-head">
            <h2>User management</h2>
            <button type="button" className="btn btn-outline btn-sm" onClick={loadUsers} disabled={usersLoading}>
              Refresh
            </button>
          </div>

          {usersLoading ? (
            <p className="admin-dashboard-status" role="status">
              Loading users…
            </p>
          ) : null}

          {!usersLoading && usersError ? (
            <div className="admin-dashboard-error" role="alert">
              <p>{usersError}</p>
              <button type="button" className="btn btn-outline btn-sm" onClick={loadUsers}>
                Retry
              </button>
            </div>
          ) : null}

          {!usersLoading && !usersError && users.length === 0 ? (
            <p className="admin-dashboard-status">No users found.</p>
          ) : null}

          {!usersLoading && !usersError && users.length > 0 ? (
            <div className="admin-users-table-wrap admin-users-table-wrap--users">
              <table className="admin-users-table admin-users-table--users">
                <thead>
                  <tr>
                    <SortableHeader
                      label="Email"
                      sortKey="email"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Name"
                      sortKey="displayName"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                      className="admin-col--hide-mobile"
                    />
                    <th className="admin-col--hide-mobile">UID</th>
                    <SortableHeader
                      label="Joined"
                      sortKey="creationTime"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Last sign-in"
                      sortKey="lastSignInTime"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Verified"
                      sortKey="emailVerified"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Credits"
                      sortKey="credits"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Projects"
                      sortKey="projectCount"
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <th className="admin-col--hide-mobile">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((row) => {
                    const isSelf = row.uid === user?.uid;
                    const isExpanded = selectedUserUid === row.uid;
                    return (
                      <Fragment key={row.uid}>
                        <tr className={isExpanded ? 'is-expanded' : ''}>
                        <td>
                          <span className="admin-user-email" title={row.email || undefined}>
                            {truncateEmail(row.email)}
                          </span>
                        </td>
                        <td className="admin-col--hide-mobile">{row.displayName || '—'}</td>
                        <td className="admin-col--hide-mobile">
                          <code className="admin-uid" title={row.uid}>
                            {truncateUid(row.uid)}
                          </code>
                        </td>
                        <td data-label="Join">
                          <span className="admin-cell-date admin-cell-date--full">{formatDate(row.creationTime)}</span>
                          <span className="admin-cell-date admin-cell-date--short">{formatDateShort(row.creationTime)}</span>
                        </td>
                        <td data-label="Login">
                          <span className="admin-cell-date admin-cell-date--full">{formatDate(row.lastSignInTime)}</span>
                          <span className="admin-cell-date admin-cell-date--short">{formatDateShort(row.lastSignInTime)}</span>
                        </td>
                        <td data-label="Ver.">
                          <span className={`admin-badge${row.emailVerified ? ' admin-badge--ok' : ''}`}>
                            {row.emailVerified ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td data-label="Cred.">{row.credits ?? 0}</td>
                        <td data-label="Proj.">
                          <button
                            type="button"
                            className="admin-project-count-btn"
                            onClick={() => handleViewProjects(row.uid)}
                            title="View projects"
                          >
                            {row.projectCount ?? 0}
                          </button>
                        </td>
                        <td className="admin-user-actions-cell">
                          <div className="admin-row-actions admin-row-actions--icons">
                            <button
                              type="button"
                              className={`admin-icon-btn${isExpanded ? ' is-active' : ''}`}
                              onClick={() => handleViewProjects(row.uid)}
                              aria-label="View projects"
                              title="View projects"
                            >
                              <FolderOpen size={16} strokeWidth={2} aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="admin-icon-btn"
                              onClick={() => openCreditsModal(row)}
                              aria-label="Edit credits"
                              title="Edit credits"
                            >
                              <Coins size={16} strokeWidth={2} aria-hidden />
                            </button>
                            {!isSelf ? (
                              <button
                                type="button"
                                className="admin-icon-btn admin-icon-btn--danger"
                                onClick={() => openDeleteModal(row)}
                                aria-label="Delete user"
                                title="Delete user"
                              >
                                <Trash2 size={16} strokeWidth={2} aria-hidden />
                              </button>
                            ) : (
                              <span className="admin-self-note">You</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr className="admin-projects-expand-row">
                          <td colSpan={USER_TABLE_COL_COUNT}>
                            <AdminUserProjectsExpand
                              loading={projectsLoading}
                              error={projectsError}
                              projects={projects}
                              onRetry={() => handleViewProjects(row.uid)}
                              onViewImage={(url, label) => setImagePreview({ url, label })}
                            />
                          </td>
                        </tr>
                      ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <section className="admin-page-section admin-dashboard-section">
          <div className="admin-dashboard-section-head">
            <h2>Idle Zero-Credit Project Cleanup</h2>
            <div className="admin-row-actions">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={handleCleanupPreview}
                disabled={cleanupPreviewLoading || cleanupRunSaving}
              >
                {cleanupPreviewLoading ? 'Previewing…' : 'Preview'}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm admin-btn-danger"
                onClick={openCleanupRunModal}
                disabled={cleanupPreviewLoading || cleanupRunSaving}
              >
                Manual Run
              </button>
            </div>
          </div>
          <p className="admin-dashboard-status">
            Users with 0 credits who have not logged in for more than 7 days will keep their account,
            but their projects and stored images may be deleted to save storage.
          </p>

          {cleanupPreviewError ? (
            <div className="admin-dashboard-error" role="alert">
              <p>{cleanupPreviewError}</p>
            </div>
          ) : null}

          {lastCleanupResult ? (
            <div className="admin-cleanup-summary">
              <p className="admin-dashboard-status">
                Last result ({lastCleanupResult.dryRun ? 'preview' : 'run'}): cutoff{' '}
                {formatDate(lastCleanupResult.cutoffDate)} · matched users{' '}
                {lastCleanupResult.matchedUsersCount ?? 0} · projects{' '}
                {lastCleanupResult.deletedProjectsCount ?? 0} · storage files{' '}
                {lastCleanupResult.deletedStorageFilesCount ?? 0}
              </p>
              {Array.isArray(lastCleanupResult.warnings) && lastCleanupResult.warnings.length > 0 ? (
                <div className="admin-dashboard-error" role="alert">
                  <p>Warnings:</p>
                  <ul className="admin-page-links">
                    {lastCleanupResult.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {Array.isArray(lastCleanupResult.users) && lastCleanupResult.users.length > 0 ? (
                <div className="admin-users-table-wrap">
                  <table className="admin-users-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Credits</th>
                        <th>Last sign-in</th>
                        <th>Projects</th>
                        <th>Deleted projects</th>
                        <th>Deleted files</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastCleanupResult.users.map((row) => (
                        <tr key={row.uid}>
                          <td>{row.email || '—'}</td>
                          <td>{row.credits ?? 0}</td>
                          <td>{formatDate(row.lastSignInTime)}</td>
                          <td>{row.projectCount ?? 0}</td>
                          <td>{row.deletedProjectsCount ?? 0}</td>
                          <td>{row.deletedStorageFilesCount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="admin-dashboard-status">No matching users in the last result.</p>
              )}
            </div>
          ) : null}
        </section>

        <section className="admin-page-section">
          <h2>App shortcuts</h2>
          <ul className="admin-page-links">
            <li>
              <Link to="/projects">Projects</Link>
            </li>
            <li>
              <Link to="/ai-tools/ai-room-decoration">New design</Link>
            </li>
            <li>
              <Link to="/credits">Credits</Link>
            </li>
          </ul>
        </section>

        <section className="admin-page-section">
          <h2>Dev &amp; test tools</h2>
          <ul className="admin-page-links">
            <li>
              <Link to="/test-pages">Test Pages</Link>
            </li>
          </ul>
        </section>

        <section className="admin-page-section">
          <h2>External</h2>
          <ul className="admin-page-links">
            <li>
              <a
                href="https://console.firebase.google.com/project/myroomsai"
                target="_blank"
                rel="noopener noreferrer"
              >
                Firebase Console
                <ExternalLink size={14} strokeWidth={2} aria-hidden />
              </a>
            </li>
          </ul>
        </section>
      </main>
      <Footer />

      {creditsModalUser ? (
        <div
          className="delete-project-confirm-backdrop"
          role="presentation"
          onClick={() => !creditsSaving && closeCreditsModal()}
        >
          <div
            className="card delete-project-confirm-dialog admin-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={creditsDialogTitleId}
            aria-describedby={creditsDialogDescId}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={creditsDialogTitleId} className="delete-project-confirm-title">
              Edit credits
            </h2>
            <p id={creditsDialogDescId} className="delete-project-confirm-desc">
              Set credits for <strong>{creditsModalUser.email || creditsModalUser.uid}</strong>.
            </p>
            <label className="admin-modal-label" htmlFor="admin-credits-input">
              Credits
            </label>
            <input
              id="admin-credits-input"
              type="number"
              min="0"
              step="1"
              className="admin-modal-input"
              value={creditsInput}
              onChange={(e) => setCreditsInput(e.target.value)}
              disabled={creditsSaving}
            />
            {creditsModalError ? (
              <p className="admin-modal-error" role="alert">
                {creditsModalError}
              </p>
            ) : null}
            <div className="delete-project-confirm-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={closeCreditsModal}
                disabled={creditsSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitCredits}
                disabled={creditsSaving}
              >
                {creditsSaving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteModalUser ? (
        <div
          className="delete-project-confirm-backdrop"
          role="presentation"
          onClick={() => !deleteSaving && closeDeleteModal()}
        >
          <div
            className="card delete-project-confirm-dialog admin-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={deleteDialogTitleId}
            aria-describedby={deleteDialogDescId}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={deleteDialogTitleId} className="delete-project-confirm-title">
              Delete user
            </h2>
            <p id={deleteDialogDescId} className="delete-project-confirm-desc">
              This permanently deletes{' '}
              <strong>{deleteModalUser.email || deleteModalUser.uid}</strong>, their Firestore user
              doc, Auth account, projects, and storage files referenced on those projects. Type{' '}
              <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              className="admin-modal-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              disabled={deleteSaving}
              autoComplete="off"
            />
            {deleteModalError ? (
              <p className="admin-modal-error" role="alert">
                {deleteModalError}
              </p>
            ) : null}
            <div className="delete-project-confirm-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={closeDeleteModal}
                disabled={deleteSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary delete-project-confirm-delete"
                onClick={submitDelete}
                disabled={deleteSaving || deleteConfirmText.trim() !== 'DELETE'}
              >
                {deleteSaving ? 'Deleting…' : 'Delete user'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {cleanupRunModalOpen ? (
        <div
          className="delete-project-confirm-backdrop"
          role="presentation"
          onClick={() => !cleanupRunSaving && closeCleanupRunModal()}
        >
          <div
            className="card delete-project-confirm-dialog admin-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={cleanupRunDialogTitleId}
            aria-describedby={cleanupRunDialogDescId}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={cleanupRunDialogTitleId} className="delete-project-confirm-title">
              Run idle zero-credit cleanup
            </h2>
            <p id={cleanupRunDialogDescId} className="delete-project-confirm-desc">
              This deletes projects and stored images for users with 0 credits who have not logged in
              for more than 7 days. Accounts and user profiles are kept. Type{' '}
              <strong>{CLEANUP_CONFIRM_PHRASE}</strong> to confirm.
            </p>
            <input
              type="text"
              className="admin-modal-input"
              value={cleanupConfirmText}
              onChange={(e) => setCleanupConfirmText(e.target.value)}
              placeholder={`Type ${CLEANUP_CONFIRM_PHRASE}`}
              disabled={cleanupRunSaving}
              autoComplete="off"
            />
            {cleanupRunError ? (
              <p className="admin-modal-error" role="alert">
                {cleanupRunError}
              </p>
            ) : null}
            <div className="delete-project-confirm-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={closeCleanupRunModal}
                disabled={cleanupRunSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary delete-project-confirm-delete"
                onClick={submitCleanupRun}
                disabled={
                  cleanupRunSaving || cleanupConfirmText.trim() !== CLEANUP_CONFIRM_PHRASE
                }
              >
                {cleanupRunSaving ? 'Running…' : 'Run cleanup'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {imagePreview ? (
        <div
          className="admin-image-lightbox-backdrop"
          role="presentation"
          onClick={() => setImagePreview(null)}
        >
          <div
            className="admin-image-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={imagePreview.label}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="admin-image-lightbox-close"
              onClick={() => setImagePreview(null)}
              aria-label="Close image preview"
            >
              <X size={20} strokeWidth={2} aria-hidden />
            </button>
            <p className="admin-image-lightbox-label">{imagePreview.label}</p>
            <img src={imagePreview.url} alt={imagePreview.label} className="admin-image-lightbox-img" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminPage;
